<app-generic-popup
  [visible]="visible()"
  (visibleChange)="visible.set($event)"
  [title]="title"
  width="1240px"
  height="860px"
  maxHeight="95vh"
  [fill]="true"
  [allowOutsideClick]="false"
  [leftButtons]="leftButtons"
  [buttons]="buttons"
  (onShowing)="open()"
>
  @if (ctx) {
    <div class="rules-body">
      <!-- ============ rule list ============ -->
      <div class="rule-list">
        <div class="add-wrap">
          <app-gpd-button
            class="add-button"
            type="primary"
            i18n-text="@@ruleAdd"
            text="Add rule"
            icon="chevron-down"
            iconPosition="after"
            (onClick)="toggleAddMenu()"
          ></app-gpd-button>
          @if (addMenuOpen) {
            <div class="add-menu" role="menu">
              @if (!copyMode) {
                <button type="button" role="menuitem" (click)="addEmptyRule()">
                  <app-svg-icon icon="add" [size]="16"></app-svg-icon>
                  <span i18n="@@ruleAddEmpty">Empty rule</span>
                </button>
                <button type="button" role="menuitem" (click)="copyMode = true">
                  <app-svg-icon icon="copy" [size]="16"></app-svg-icon>
                  <span i18n="@@ruleAddCopy">Copy existing rule</span>
                </button>
              } @else {
                <button type="button" class="menu-back" (click)="copyMode = false">
                  <app-svg-icon icon="arrow-left" [size]="16"></app-svg-icon>
                  <span i18n="@@ruleAddCopy">Copy existing rule</span>
                </button>
                <div class="copy-search">
                  <dx-text-box
                    class="gpd-input"
                    [(value)]="copySearch"
                    valueChangeEvent="input"
                    mode="search"
                    i18n-placeholder="@@ruleSearch"
                    placeholder="Search rule …"
                  ></dx-text-box>
                </div>
                <div class="copy-list">
                  @for (candidate of copyCandidates; track $index) {
                    <button type="button" role="menuitem" class="copy-item" (click)="copyRule(candidate.rule)">
                      <span class="copy-name">{{ candidate.rule.name }}</span>
                      <span class="copy-task" [attr.title]="candidate.tasks.length > 1 ? copyTaskTitles(candidate.tasks) : null">{{ copyTaskText(candidate.tasks) }}</span>
                    </button>
                  } @empty {
                    <div class="copy-empty" i18n="@@ruleNoRuleFound">No rule found.</div>
                  }
                </div>
              }
            </div>
          }
        </div>

        @for (rule of rules; track rule._key) {
          <div
            class="rule-card"
            role="button"
            tabindex="0"
            [class.selected]="rule._key === selectedKey"
            [class.inactive]="!rule.isActive"
            [class.invalid]="isInvalid(rule)"
            (click)="select(rule)"
            (keydown.enter)="select(rule)"
          >
            <div class="rule-card-top">
              <span class="rule-card-name">{{ rule.name || noName }}</span>
              <button
                type="button"
                class="toggle"
                [class.on]="rule.isActive"
                [attr.aria-pressed]="rule.isActive"
                i18n-aria-label="@@ruleActive"
                aria-label="Active"
                (click)="toggleActive(rule, $event)"
              ></button>
            </div>
            <div class="rule-card-summary">{{ summary(rule) }}</div>
          </div>
        } @empty {
          <div class="rule-list-empty" i18n="@@ruleListEmpty">No rules yet.</div>
        }
      </div>

      <!-- ============ editor ============ -->
      <div class="editor" [class.scrolled]="editorScrolled" (scroll)="editorScrolled = $any($event.target).scrollTop > 0">
        @if (selected; as rule) {
          <app-validation-toast
            class="preview"
            state="Info"
            [inline]="true"
            [persistent]="true"
            [autoTitle]="previewTitle"
            [autoMessage]="preview"
          ></app-validation-toast>

          @if (rule.migrationHint) {
            <div class="info-note">
              <app-svg-icon icon="info" [size]="16"></app-svg-icon>
              <span>{{ rule.migrationHint }}</span>
            </div>
          }

          <!-- 1 General -->
          <section class="sec">
            <div class="sec-head">
              <span class="sec-num">1</span><h3 i18n="@@ruleGeneral">General</h3>
              <div class="toggle-row head-toggle">
                <button type="button" class="toggle" role="switch" [class.on]="rule.isActive" [attr.aria-checked]="rule.isActive" (click)="toggleActive(rule)"></button>
                @if (rule.isActive) {
                  <span i18n="@@ruleActive">Active</span>
                } @else {
                  <span i18n="@@ruleInactive">Inactive</span>
                }
              </div>
            </div>
            <div class="sec-body">
              <div>
                <label class="gpd-form-label gpd-form-required" i18n="@@ruleName">Name of the rule</label>
                <dx-text-box
                  class="gpd-input"
                  [class.err]="showErrors && !rule.name.trim()"
                  [(value)]="rule.name"
                  valueChangeEvent="input"
                  [maxLength]="200"
                  i18n-placeholder="@@ruleNamePlaceholder"
                  placeholder="e.g. Before due date"
                ></dx-text-box>
                @if (showErrors && !rule.name.trim()) {
                  <div class="hint err">{{ ERR.name }}</div>
                }
              </div>
            </div>
          </section>

          <!-- 2 Conditions -->
          <section class="sec">
            <div class="sec-head"><span class="sec-num">2</span><h3 i18n="@@ruleConditions">Conditions</h3></div>
            <div class="sec-body">
              @for (group of rule.definition.groups; track $index; let gi = $index) {
                @if (gi > 0) {
                  <div class="or-wrap"><span class="join or" i18n="@@ruleOr">OR</span></div>
                }
                <div class="cond-list" [class.grp]="multiGroup">
                  @if (multiGroup) {
                    <div class="grp-head">
                      <span i18n="@@ruleGroup">Group {{ gi + 1 }}</span>
                      <button type="button" class="trash" i18n-aria-label="@@ruleRemoveGroup" aria-label="Remove group" (click)="removeGroup(gi)">
                        <app-svg-icon icon="delete" [size]="18" color="#f75050"></app-svg-icon>
                      </button>
                    </div>
                  }
                  @for (c of group.conditions; track $index; let ci = $index) {
                    <div class="cond-item">
                      <div class="row">
                        @if (ci === 0) {
                          <span class="join" i18n="@@ruleIf">IF</span>
                        } @else {
                          <span class="join" i18n="@@ruleAnd">AND</span>
                        }
                        <dx-select-box
                          class="gpd-input grow"
                          [class.err]="!!conditionError(c) && (!c.field || conditionError(c) === ERR.notAvailable)"
                          [items]="fieldOptions(c)"
                          displayExpr="text"
                          valueExpr="value"
                          [value]="c.field"
                          (onValueChanged)="$event.event && onFieldChanged(c, $event.value)"
                          i18n-placeholder="@@pleaseSelect"
                          placeholder="Please Select"
                        ></dx-select-box>

                        @if (c.field === 'OtherTask') {
                          <dx-select-box
                            class="gpd-input grow"
                            [class.err]="selfReference(c) || (!!conditionError(c) && c.otherTaskConfigId == null)"
                            [items]="otherTaskOptions(c)"
                            displayExpr="text"
                            valueExpr="value"
                            [value]="c.otherTaskConfigId"
                            [searchEnabled]="true"
                            (onValueChanged)="$event.event && onOtherTaskChanged(c, $event.value)"
                            i18n-placeholder="@@ruleTask"
                            placeholder="Task"
                          ></dx-select-box>
                          @if (c.otherTaskConfigId != null) {
                            <dx-select-box
                              class="gpd-input fixed"
                              [items]="propertyOptions(c)"
                              displayExpr="text"
                              valueExpr="value"
                              [value]="c.property"
                              (onValueChanged)="$event.event && onPropertyChanged(c, $event.value)"
                            ></dx-select-box>
                          }
                        }

                        @if (showOperator(c)) {
                          <dx-select-box
                            class="gpd-input grow"
                            [class.err]="!!conditionError(c) && !c.operator"
                            [items]="operatorOptions(c)"
                            displayExpr="text"
                            valueExpr="value"
                            [value]="c.operator"
                            (onValueChanged)="$event.event && onOperatorChanged(c, $event.value)"
                            i18n-placeholder="@@pleaseSelect"
                            placeholder="Please Select"
                          ></dx-select-box>
                        }

                        @switch (valueKind(c)) {
                          @case ('duration') {
                            <ng-container *ngTemplateOutlet="durationTpl; context: { $implicit: c }"></ng-container>
                          }
                          @case ('durationStatus') {
                            <ng-container *ngTemplateOutlet="durationTpl; context: { $implicit: c }"></ng-container>
                            <ng-container *ngTemplateOutlet="statusTpl; context: { $implicit: c }"></ng-container>
                          }
                          @case ('status') {
                            <ng-container *ngTemplateOutlet="statusTpl; context: { $implicit: c }"></ng-container>
                          }
                          @case ('number') {
                            <dx-text-box class="gpd-input num" [class.err]="!!conditionError(c)" [(value)]="c.value" valueChangeEvent="input" i18n-placeholder="@@ruleValue" placeholder="Value"></dx-text-box>
                          }
                          @case ('numberRange') {
                            <dx-text-box class="gpd-input num" [class.err]="!!conditionError(c)" [(value)]="c.value" valueChangeEvent="input" i18n-placeholder="@@ruleValue" placeholder="Value"></dx-text-box>
                            <span class="txt" i18n="@@ruleAndLower">and</span>
                            <dx-text-box class="gpd-input num" [class.err]="!!conditionError(c)" [(value)]="c.value2" valueChangeEvent="input" i18n-placeholder="@@ruleValue" placeholder="Value"></dx-text-box>
                          }
                          @case ('text') {
                            <dx-text-box class="gpd-input grow" [class.err]="!!conditionError(c)" [(value)]="c.value" valueChangeEvent="input" i18n-placeholder="@@text" placeholder="Text"></dx-text-box>
                          }
                          @case ('option') {
                            <dx-select-box class="gpd-input grow" [class.err]="!!conditionError(c)" [items]="selectionOptions(c)" displayExpr="text" valueExpr="value" [(value)]="c.value" i18n-placeholder="@@ruleFieldSelection" placeholder="Option"></dx-select-box>
                          }
                          @case ('contractType') {
                            <dx-select-box class="gpd-input grow" [class.err]="!!conditionError(c)" [items]="contractTypeOptions(c)" displayExpr="text" valueExpr="value" [(value)]="c.value" [searchEnabled]="true" i18n-placeholder="@@ruleFieldContractType" placeholder="Contract type"></dx-select-box>
                          }
                          @case ('contractStatus') {
                            <dx-select-box class="gpd-input grow" [class.err]="!!conditionError(c)" [items]="contractStatusOptions(c)" displayExpr="text" valueExpr="value" [(value)]="c.value" i18n-placeholder="@@ruleFieldContractStatus" placeholder="Contract status"></dx-select-box>
                          }
                          @case ('contact') {
                            <div class="grow contact-box" [class.err]="!!conditionError(c)">
                              <app-contacts-dropdown-box
                                [companies]="companies"
                                [persons]="persons"
                                [contactId]="c.value ? +c.value : null"
                                [contactType]="c.contactType"
                                (onContactSelected)="onContactSelected(c, $event)"
                              ></app-contacts-dropdown-box>
                            </div>
                          }
                        }

                        <button type="button" class="trash" i18n-aria-label="@@ruleRemoveCondition" aria-label="Remove condition" (click)="removeCondition(group, ci)">
                          <app-svg-icon icon="delete" [size]="18" color="#f75050"></app-svg-icon>
                        </button>
                      </div>
                      @if (selfReference(c)) {
                        <div class="hint err row-note">{{ ERR.self }}</div>
                      } @else if (conditionError(c)) {
                        <div class="hint err row-note">{{ conditionError(c) }}</div>
                      }
                    </div>
                  }
                  @if (multiGroup) {
                    <div>
                      <button type="button" class="link-btn" (click)="addCondition(group)" i18n="@@ruleAddCondition">+ Add condition</button>
                    </div>
                  }
                </div>
              }
              @if (showErrors && rule.definition.groups.length === 0) {
                <div class="hint err">{{ ERR.noConditions }}</div>
              }
              <div class="link-row">
                @if (!multiGroup) {
                  <button type="button" class="link-btn" (click)="rule.definition.groups.length ? addCondition(rule.definition.groups[0]) : addGroup()" i18n="@@ruleAddCondition">+ Add condition</button>
                }
                <button type="button" class="link-btn" (click)="addGroup()" i18n="@@ruleAddOrGroup">+ Add OR group</button>
              </div>
            </div>
          </section>

          <!-- 3 Actions -->
          <section class="sec">
            <div class="sec-head"><span class="sec-num">3</span><h3 i18n="@@ruleActions">Actions</h3></div>
            <div class="sec-body action-list">
              @for (a of rule.definition.actions; track $index; let ai = $index) {
                <div class="action-item">
                  <div class="row">
                    @if (ai === 0) {
                      <span class="join" i18n="@@ruleThen">THEN</span>
                    } @else {
                      <span class="join" i18n="@@ruleAnd">AND</span>
                    }
                    <dx-select-box
                      class="gpd-input grow"
                      [class.err]="!!actionError(a) && (!a.type || actionError(a) === ERR.notAvailable)"
                      [items]="actionOptions(a)"
                      displayExpr="text"
                      valueExpr="value"
                      [value]="a.type"
                      (onValueChanged)="$event.event && onActionTypeChanged(a, $event.value)"
                      i18n-placeholder="@@pleaseSelect"
                      placeholder="Please Select"
                    ></dx-select-box>

                    @switch (a.type) {
                      @case ('Notify') {
                        <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a) && !a.recipient" [items]="RECIPIENTS" displayExpr="text" valueExpr="value" [(value)]="a.recipient" i18n-placeholder="@@pleaseSelect" placeholder="Please Select"></dx-select-box>
                        @if (a.recipient === 'Department') {
                          <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a) && a.departmentId == null" [items]="departmentOptions()" displayExpr="text" valueExpr="value" [(value)]="a.departmentId" [searchEnabled]="true" i18n-placeholder="@@department" placeholder="Department"></dx-select-box>
                        }
                        @if (a.recipient === 'User') {
                          <ng-container *ngTemplateOutlet="userTpl; context: { $implicit: a }"></ng-container>
                        }
                      }
                      @case ('SetStatus') {
                        <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a)" [items]="statusOptions(a)" displayExpr="text" valueExpr="value" [(value)]="a.status" i18n-placeholder="@@status" placeholder="Status"></dx-select-box>
                      }
                      @case ('SetResponsible') {
                        <dx-select-box class="gpd-input fixed" [items]="responsibleKinds" displayExpr="text" valueExpr="value" [value]="responsibleKind(a)" (onValueChanged)="$event.event && onResponsibleKindChanged(a, $event.value)"></dx-select-box>
                        @if (responsibleKind(a) === 'User') {
                          <ng-container *ngTemplateOutlet="userTpl; context: { $implicit: a }"></ng-container>
                        } @else {
                          <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a)" [items]="departmentOptions()" displayExpr="text" valueExpr="value" [(value)]="a.departmentId" [searchEnabled]="true" i18n-placeholder="@@department" placeholder="Department"></dx-select-box>
                        }
                      }
                      @case ('SetResult') {
                        @switch (ctx.task.type) {
                          @case (TaskTypeEnum.YesNo) {
                            <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a)" [items]="yesNoOptions" displayExpr="text" valueExpr="value" [(value)]="a.value" i18n-placeholder="@@ruleFieldSelection" placeholder="Option"></dx-select-box>
                          }
                          @case (TaskTypeEnum.UserDefinedFields) {
                            <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a)" [items]="ctx.task.options" [(value)]="a.value" i18n-placeholder="@@ruleFieldSelection" placeholder="Option"></dx-select-box>
                          }
                          @default {
                            <dx-text-box class="gpd-input grow" [class.err]="!!actionError(a)" [(value)]="a.value" valueChangeEvent="input" i18n-placeholder="@@ruleResult" placeholder="Result"></dx-text-box>
                          }
                        }
                      }
                      @case ('SetPriority') {
                        <dx-select-box class="gpd-input grow" [class.err]="!!actionError(a)" [items]="PRIORITIES" displayExpr="text" valueExpr="value" [(value)]="a.priority" i18n-placeholder="@@priority" placeholder="Priority"></dx-select-box>
                      }
                    }

                    <button type="button" class="trash" i18n-aria-label="@@ruleRemoveAction" aria-label="Remove action" (click)="removeAction(ai)">
                      <app-svg-icon icon="delete" [size]="18" color="#f75050"></app-svg-icon>
                    </button>
                  </div>

                  @if (a.type === 'Notify') {
                    <div class="row-note">
                      <dx-text-area
                        class="gpd-input comment"
                        [(value)]="a.comment"
                        valueChangeEvent="input"
                        [autoResizeEnabled]="true"
                        [minHeight]="30"
                        [maxLength]="2000"
                        i18n-placeholder="@@ruleCommentPlaceholder"
                        placeholder="Optional: comment"
                      ></dx-text-area>
                    </div>
                  }
                  @if (a.type === 'CreateMicrotask') {
                    <div class="mt-grid">
                      <div>
                        <label class="gpd-form-label gpd-form-required" i18n="@@title">Title</label>
                        <dx-text-box class="gpd-input" [class.err]="!!actionError(a) && !a.title" [(value)]="a.title" valueChangeEvent="input"></dx-text-box>
                      </div>
                      <div>
                        <label class="gpd-form-label gpd-form-required" i18n="@@ruleTaskPackage">Task package</label>
                        <dx-select-box class="gpd-input" [class.err]="!!actionError(a) && a.taskPackageId == null" [items]="taskPackages" displayExpr="name" valueExpr="id" [(value)]="a.taskPackageId" [searchEnabled]="true" i18n-placeholder="@@pleaseSelect" placeholder="Please Select"></dx-select-box>
                      </div>
                      <div>
                        <label class="gpd-form-label gpd-form-required" i18n="@@type">Type</label>
                        <dx-select-box class="gpd-input" [class.err]="!!actionError(a) && !a.taskType" [items]="MICROTASK_TYPES" displayExpr="text" valueExpr="value" [(value)]="a.taskType" i18n-placeholder="@@pleaseSelect" placeholder="Please Select"></dx-select-box>
                      </div>
                      <div>
                        <label class="gpd-form-label gpd-form-required" i18n="@@department">Department</label>
                        <dx-select-box class="gpd-input" [class.err]="!!actionError(a) && a.departmentId == null" [items]="departmentOptions()" displayExpr="text" valueExpr="value" [(value)]="a.departmentId" [searchEnabled]="true" i18n-placeholder="@@pleaseSelect" placeholder="Please Select"></dx-select-box>
                      </div>
                      <div class="full">
                        <app-gpd-checkbox
                          i18n-label="@@validationRequired"
                          label="Validation Required"
                          [value]="!!a.validationRequired"
                          (valueChange)="a.validationRequired = $event"
                        ></app-gpd-checkbox>
                      </div>
                    </div>
                    <div class="hint row-note mt-hint" i18n="@@ruleMicrotaskHint">Created once. If the condition no longer applies, the microtask stays.</div>
                  }
                  @if (actionError(a)) {
                    <div class="hint err row-note">{{ actionError(a) }}</div>
                  }
                  @for (other of conflicts(a); track $index) {
                    <div class="conflict row-note">
                      <app-svg-icon icon="alert" [size]="16"></app-svg-icon>
                      <span>{{ conflictText(a, other) }}</span>
                    </div>
                  }
                </div>
              }

              @if (showReviewToast) {
                <div class="toast-warn">
                  <app-svg-icon icon="alert" [size]="22" color="#f4b400"></app-svg-icon>
                  <div>
                    <div class="tw-title" i18n="@@ruleReviewToastTitle">A review is active for this task</div>
                    <div class="tw-text" i18n="@@ruleReviewToastText">The result moves the task to “In review” and its reviewer is asked to check it as usual. Only after approval is it “Done”; if rejected, “Rejected”. A result already entered by hand is never overwritten.</div>
                  </div>
                </div>
              }
              @if (showErrors && rule.definition.actions.length === 0) {
                <div class="hint err">{{ ERR.noActions }}</div>
              }
              <div>
                <button type="button" class="link-btn" (click)="addAction()" i18n="@@ruleAddAction">+ Add action</button>
              </div>
            </div>
          </section>

          <!-- 4 Timing -->
          <section class="sec">
            <div class="sec-head"><span class="sec-num">4</span><h3 i18n="@@ruleTiming">Timing</h3></div>
            <div class="sec-body">
              <div class="time-cols">
                <div class="time-block">
                  <div class="gpd-form-label" i18n="@@ruleTrigger">Trigger</div>
                  <div class="sentence">
                    <dx-select-box class="gpd-input wide" [items]="triggers" displayExpr="text" valueExpr="value" [(value)]="rule.definition.timing.trigger"></dx-select-box>
                    @if (rule.definition.timing.trigger === 'Delayed') {
                      <dx-number-box class="gpd-input num" [class.err]="!!timingErrors().delay" [(value)]="rule.definition.timing.delayAmount" [min]="1" format="#0" [showSpinButtons]="false" valueChangeEvent="input" i18n-placeholder="@@ruleAmount" placeholder="Number"></dx-number-box>
                      <dx-select-box class="gpd-input unit" [items]="UNITS" displayExpr="text" valueExpr="value" [(value)]="rule.definition.timing.delayUnit"></dx-select-box>
                      <span i18n="@@ruleAfterConditions">after the conditions are met.</span>
                    }
                  </div>
                  @if (timingErrors().delay) {
                    <div class="hint err">{{ timingErrors().delay }}</div>
                  }
                </div>
                <div class="time-block" [attr.title]="locked ? lockedHint : null">
                  <div class="gpd-form-label" i18n="@@ruleRepetition">Repetition</div>
                  <div class="sentence">
                    @if (locked) {
                      <dx-select-box class="gpd-input wide" [items]="repeats" displayExpr="text" valueExpr="value" value="Once" [disabled]="true"></dx-select-box>
                    } @else {
                      <dx-select-box class="gpd-input wide" [items]="repeats" displayExpr="text" valueExpr="value" [value]="rule.definition.timing.repeat" (onValueChanged)="onRepeatChanged($event.value)"></dx-select-box>
                      @if (rule.definition.timing.repeat === 'Repeat') {
                        <dx-select-box class="gpd-input fixed" [class.err]="!!timingErrors().interval" [items]="intervals" displayExpr="text" valueExpr="value" [value]="rule.definition.timing.interval" (onValueChanged)="onIntervalChanged($event.value)"></dx-select-box>
                        @if (rule.definition.timing.interval === 'Custom') {
                          <span i18n="@@ruleEvery">every</span>
                          <dx-number-box class="gpd-input num" [class.err]="!!timingErrors().everyDays" [(value)]="rule.definition.timing.everyDays" [min]="2" format="#0" [showSpinButtons]="false" valueChangeEvent="input"></dx-number-box>
                          <span i18n="@@ruleUnitDays">Days</span>
                        }
                        <span i18n="@@ruleMax">max.</span>
                        <dx-number-box class="gpd-input num" [class.err]="!!timingErrors().max" [(value)]="rule.definition.timing.maxNotifications" [min]="2" format="#0" [showSpinButtons]="false" valueChangeEvent="input"></dx-number-box>
                        <span i18n="@@ruleNotifications">notifications</span>
                      }
                    }
                  </div>
                  @if (timingErrors().everyDays || timingErrors().max) {
                    <div class="hint err">{{ timingErrors().everyDays ?? timingErrors().max }}</div>
                  }
                </div>
              </div>
            </div>
          </section>
        } @else {
          <div class="empty">
            <b i18n="@@ruleNoRuleYet">No rule yet</b>
            <span i18n="@@ruleNoRuleYetHint">Add a rule on the left – empty or as a copy of an existing one.</span>
          </div>
        }
      </div>
    </div>
  }
</app-generic-popup>

<ng-template #durationTpl let-c>
  <dx-number-box
    class="gpd-input num"
    [class.err]="conditionError(c) === ERR.amount || conditionError(c) === ERR.wholeNumber"
    [(value)]="c.amount"
    [min]="0"
    format="#0"
    [showSpinButtons]="false"
    valueChangeEvent="input"
    i18n-placeholder="@@ruleAmount"
    placeholder="Number"
  ></dx-number-box>
  <dx-select-box class="gpd-input unit" [items]="UNITS" displayExpr="text" valueExpr="value" [(value)]="c.unit"></dx-select-box>
</ng-template>

<ng-template #statusTpl let-c>
  <dx-select-box
    class="gpd-input grow"
    [class.err]="!!conditionError(c) && !c.value"
    [items]="STATUSES"
    displayExpr="text"
    valueExpr="value"
    [(value)]="c.value"
    i18n-placeholder="@@status"
    placeholder="Status"
  ></dx-select-box>
</ng-template>

<ng-template #userTpl let-a>
  <dx-select-box
    class="gpd-input grow"
    [class.err]="!!actionError(a) && !a.userEmail"
    [items]="users"
    displayExpr="displayName"
    valueExpr="emailAddress"
    [(value)]="a.userEmail"
    [searchEnabled]="true"
    searchExpr="displayName"
    i18n-placeholder="@@ruleUser"
    placeholder="User"
  ></dx-select-box>
</ng-template>

<app-delete-popup
  [visible]="deleteConfirmVisible"
  (visibleChange)="deleteConfirmVisible = $event"
  [title]="deleteTitle"
  [message]="deleteMessage"
  [confirmText]="deleteConfirmText"
  (onConfirm)="confirmDelete()"
  (onAbort)="deleteConfirmVisible = false"
></app-delete-popup>
